import { useState } from 'react';
import type { ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider, TooltipProvider } from '@taskflow/ui';
import { ThemeProvider } from '../contexts/ThemeContext';
import { AuthProvider } from '../features/auth/AuthContext';
import { ApiError } from '../services/httpClient';

/**
 * Application-wide providers.
 *
 * The QueryClient defaults below are chosen against a hard constraint: the
 * Cloudflare free plan allows 100,000 Worker requests per day, and careless
 * refetching is the fastest way to spend it. Realtime (Phase 5) pushes changes
 * into this cache, so aggressive polling is not merely wasteful — it is
 * redundant.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Data stays fresh for a minute; realtime invalidates it sooner when it
        // actually changes.
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        // Refetching on every window focus is the single biggest source of
        // wasted requests in a tab people leave open all day.
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: (failureCount, error) => {
          // Never retry a client error: a 403 will still be a 403, and retrying
          // an auth failure just burns requests and delays the redirect.
          if (error instanceof ApiError) {
            if (error.status >= 400 && error.status < 500) return false;
          }
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        // Mutations are never retried automatically — a retried create can
        // duplicate a card. Endpoints that need it send an Idempotency-Key and
        // opt in explicitly.
        retry: false,
      },
    },
  });
}

export function AppProviders({ children }: { children: ReactNode }) {
  // Created in state so React 19 Strict Mode's double-invoke does not build two
  // caches and halve the hit rate.
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        {/* AuthProvider sits inside QueryClientProvider because the session is a
            query, and outside the router because every route depends on it. */}
        <AuthProvider>
          <TooltipProvider delayDuration={400}>
            {children}
            <ToastProvider />
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
